import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { arenaRpc } from "@/lib/arena";
import { meetingBucket } from "@/lib/arena-logic";

const TITLE = "ARENA — админка TurkeyDrive";
const DESC = "Участники, встречи и стикеры фестиваля ARENA.";

export const Route = createFileRoute("/_authenticated/admin/arena")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminArenaPage,
});

type Overview = {
  participants: number;
  listed: number;
  day24: number;
  day25: number;
  both: number;
  meetings: number;
  completed_task: number;
  stickers: number;
  list: {
    user_id: string;
    full_name: string;
    src: string | null;
    joined_at: string;
    days: number[];
    sticker_given_at: string | null;
  }[];
};

const fmt = (d: string) =>
  new Date(d).toLocaleString("ru-RU", {
    timeZone: "Europe/Istanbul",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

function AdminArenaPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["arena-admin"],
    queryFn: async () => {
      const { data, error } = await arenaRpc.adminOverview();
      if (error) throw error;
      return data as Overview;
    },
  });

  const { data: meetings = [] } = useQuery({
    queryKey: ["arena-admin-meetings"],
    queryFn: async () => {
      const { data, error } = await arenaRpc.myMeetings();
      if (error) throw error;
      return (data ?? []) as { user_a: string; user_b: string; completed_at: string | null }[];
    },
  });

  if (isLoading) return <p className="p-6 text-center text-sm text-td-muted">Загружаем…</p>;
  if (error || !data)
    return <p className="p-6 text-center text-sm text-td-muted">Раздел доступен только админам</p>;

  const stats: [string, number][] = [
    ["Вступили", data.participants],
    ["В списке (прошли мастер)", data.listed ?? 0],
    ["Только 24 октября", data.day24],
    ["Только 25 октября", data.day25],
    ["Оба дня", data.both],
    ["Подтверждённых встреч", data.meetings],
    ["Выполнили задание", data.completed_task],
    ["Выдано стикеров", data.stickers],
  ];

  const perUser = new Map<string, number>();
  for (const m of meetings) {
    if (!m.completed_at) continue;
    perUser.set(m.user_a, (perUser.get(m.user_a) ?? 0) + 1);
    perUser.set(m.user_b, (perUser.get(m.user_b) ?? 0) + 1);
  }
  const buckets = ["0", "1–2", "3–4", "5–9", "10+"] as const;
  const dist = Object.fromEntries(buckets.map((b) => [b, 0])) as Record<string, number>;
  for (const r of data.list) dist[meetingBucket(perUser.get(r.user_id) ?? 0)]++;

  return (
    <main className="mx-auto w-full max-w-3xl space-y-5 px-4 py-6">
      <h1 className="font-display text-2xl font-bold text-td-dark">ARENA</h1>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {stats.map(([label, n]) => (
          <div key={label} className="rounded-xl border border-td-line p-3">
            <p className="text-2xl font-bold text-td-dark">{n}</p>
            <p className="text-xs text-td-muted">{label}</p>
          </div>
        ))}
      </div>
      <div>
        <h2 className="mb-2 text-sm font-semibold text-td-dark">Участники по числу встреч</h2>
        <div className="grid grid-cols-5 gap-2">
          {buckets.map((b) => (
            <div key={b} className="rounded-xl border border-td-line p-2 text-center">
              <p className="text-xl font-bold text-td-dark">{dist[b]}</p>
              <p className="text-xs text-td-muted">{b}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-td-line">
        <table className="w-full min-w-[520px] text-left text-sm">
          <thead className="bg-td-soft/60 text-xs text-td-muted">
            <tr>
              <th className="px-3 py-2">Имя</th>
              <th className="px-3 py-2">Источник</th>
              <th className="px-3 py-2">Присоединился</th>
              <th className="px-3 py-2">Дни</th>
              <th className="px-3 py-2">Стикер</th>
            </tr>
          </thead>
          <tbody>
            {data.list.map((r) => (
              <tr key={r.user_id} className="border-t border-td-line">
                <td className="px-3 py-2 text-td-dark">{r.full_name}</td>
                <td className="px-3 py-2 text-td-muted">{r.src ?? "—"}</td>
                <td className="px-3 py-2 text-td-muted">{fmt(r.joined_at)}</td>
                <td className="px-3 py-2 text-td-muted">{(r.days ?? []).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-td-muted">
                  {r.sticker_given_at ? `✓ ${fmt(r.sticker_given_at)}` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
